import { useState, useEffect } from 'react';
import { MdRefresh, MdDashboard, MdWarning, MdCheckCircle, MdQrCodeScanner, MdTrendingUp, MdFileDownload } from 'react-icons/md';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { getUserAnalytics, getScanAnalytics, getAccessibleLocations, getVehicleUtilization } from '../../../api/vehicleAnalytics';
import * as XLSX from 'xlsx';
import '../../../styles/VehicleDashboard.css';
import './BookRoom.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function VehicleDashboard() {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [scanData, setScanData] = useState(null);
  const [vehicleUtilData, setVehicleUtilData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    getAccessibleLocations()
      .then(res => {
        if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
          setLocations(res.data);
        }
      })
      .catch(() => {});
    loadDashboardData('');
  }, []);

  const loadDashboardData = async (location, dates) => {
    const loc = location !== undefined ? location : selectedLocation;
    const { startDate, endDate } = dates || dateRange;
    try {
      setLoading(true);
      setError('');

      const [analyticsResponse, scanResponse, utilResponse] = await Promise.all([
        getUserAnalytics(loc || undefined, startDate, endDate),
        getScanAnalytics(loc || undefined, startDate, endDate).catch(err => {
          console.warn('Scan analytics not available:', err);
          return null;
        }),
        getVehicleUtilization(startDate, endDate, loc || undefined).catch(err => {
          console.warn('Vehicle utilization not available:', err);
          return null;
        })
      ]);

      if (analyticsResponse?.data) {
        setAnalyticsData(analyticsResponse.data);
      } else {
        throw new Error(analyticsResponse?.message || 'Failed to load analytics data');
      }

      if (scanResponse?.data) setScanData(scanResponse.data);
      if (utilResponse?.data) setVehicleUtilData(utilResponse.data);
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError(err?.response?.data?.message || 'Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const exportTable = (data, name) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, name);
    XLSX.writeFile(wb, `${name}.xlsx`);
  };

  const exportScanData = () => {
    if (!scanData?.recentScans) return;
    exportTable(scanData.recentScans.map(s => ({
      'Employee ID': s.empId, 'Name': s.employeeName, 'Route': s.route,
      'Vehicle': s.vehicle, 'Timing': s.timing, 'Scan Time': s.scanTime, 'Scanned By': s.scannedBy
    })), 'Recent_Scans');
  };

  const exportRouteDetails = () => {
    if (!analyticsData?.routeAnalytics) return;
    exportTable(analyticsData.routeAnalytics.map(r => ({
      'Route': r.routeName, 'Timing': r.timing, 'Vehicles': r.assignedVehicles?.length || 0,
      'Capacity': r.totalCapacity, 'Assigned': r.assignedUsers, 'Available': r.availableSeats,
      'Utilization %': r.utilizationPercent?.toFixed(1), 'Status': r.capacityStatus
    })), 'Route_Details');
  };

  // Chart configurations
  const capacityBreakdownChartData = analyticsData?.capacityBreakdown ? {
    labels: ['At Capacity', 'Over Capacity', 'Under Utilized', 'Optimal'],
    datasets: [{
      label: 'Routes',
      data: [
        analyticsData.capacityBreakdown.routesAtCapacity || 0,
        analyticsData.capacityBreakdown.routesOverCapacity || 0,
        analyticsData.capacityBreakdown.routesUnderUtilized || 0,
        analyticsData.capacityBreakdown.routesOptimal || 0
      ],
      backgroundColor: [
        'rgba(255, 206, 86, 0.6)',    // Yellow for At Capacity
        'rgba(255, 99, 132, 0.6)',    // Red for Over Capacity
        'rgba(201, 203, 207, 0.6)',   // Gray for Under Utilized
        'rgba(75, 192, 192, 0.6)',    // Green for Optimal
      ],
      borderColor: [
        'rgba(255, 206, 86, 1)',
        'rgba(255, 99, 132, 1)',
        'rgba(201, 203, 207, 1)',
        'rgba(75, 192, 192, 1)',
      ],
      borderWidth: 1,
    }]
  } : null;

  const routeCapacityChartData = analyticsData?.routeAnalytics ? {
    labels: analyticsData.routeAnalytics.map(r => r.routeName),
    datasets: [
      {
        label: 'Capacity',
        data: analyticsData.routeAnalytics.map(r => r.totalCapacity),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
      },
      {
        label: 'Assigned Users',
        data: analyticsData.routeAnalytics.map(r => r.assignedUsers),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      }
    ]
  } : null;

  const userDistributionChartData = analyticsData?.userDistribution ? {
    labels: analyticsData.userDistribution.map(d => d.routeName),
    datasets: [{
      label: 'User Count',
      data: analyticsData.userDistribution.map(d => d.userCount),
      backgroundColor: 'rgba(153, 102, 255, 0.6)',
    }]
  } : null;

  // Scan Analytics Charts
  const dailyTrendChartData = scanData?.dailyTrend ? {
    labels: scanData.dailyTrend.map(d => {
      const date = new Date(d.date);
      return date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', month: 'short', day: 'numeric' });
    }),
    datasets: [{
      label: 'Daily Scans',
      data: scanData.dailyTrend.map(d => d.scanCount),
      fill: true,
      backgroundColor: 'rgba(99, 102, 241, 0.2)',
      borderColor: 'rgba(99, 102, 241, 1)',
      tension: 0.4,
      pointBackgroundColor: 'rgba(99, 102, 241, 1)',
    }]
  } : null;

  const routeScansChartData = scanData?.scansByRoute ? {
    labels: scanData.scansByRoute.map(r => r.route),
    datasets: [{
      label: 'Scans',
      data: scanData.scansByRoute.map(r => r.scanCount),
      backgroundColor: [
        'rgba(99, 102, 241, 0.7)',
        'rgba(236, 72, 153, 0.7)',
        'rgba(34, 197, 94, 0.7)',
        'rgba(251, 146, 60, 0.7)',
        'rgba(14, 165, 233, 0.7)',
        'rgba(168, 85, 247, 0.7)',
        'rgba(244, 63, 94, 0.7)',
        'rgba(20, 184, 166, 0.7)',
      ],
      borderWidth: 0,
    }]
  } : null;

  const timingChartData = scanData?.scansByTiming ? {
    labels: Object.keys(scanData.scansByTiming),
    datasets: [{
      label: 'Scans by Timing',
      data: Object.values(scanData.scansByTiming),
      backgroundColor: ['rgba(34, 197, 94, 0.7)', 'rgba(251, 146, 60, 0.7)'],
      borderWidth: 0,
    }]
  } : null;

  const getCapacityStatusColor = (status) => {
    switch (status) {
      case 'OVER_CAPACITY':
        return 'status-over';
      case 'AT_CAPACITY':
        return 'status-at';
      case 'OPTIMAL':
        return 'status-optimal';
      case 'UNDER_UTILIZED':
        return 'status-under';
      default:
        return '';
    }
  };

  const getCapacityStatusLabel = (status) => {
    switch (status) {
      case 'OVER_CAPACITY':
        return (
          <span>
            Over Capacity <MdWarning style={{ display: 'inline', marginLeft: '4px', color: '#ef4444' }} />
          </span>
        );
      case 'AT_CAPACITY':
        return 'At Capacity';
      case 'OPTIMAL':
        return (
          <span>
            Optimal <MdCheckCircle style={{ display: 'inline', marginLeft: '4px', color: '#10b981' }} />
          </span>
        );
      case 'UNDER_UTILIZED':
        return 'Under Utilized';
      default:
        return status;
    }
  };

  const thStyle = {
    padding: '10px 14px',
    background: '#4a2c6e',
    color: '#fff',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };

  const tdStyle = {
    padding: '10px 14px',
    fontSize: '13px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 0,
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">{error}</div>
        <button onClick={loadDashboardData} className="retry-button">Retry</button>
      </div>
    );
  }

  return (
    <div className="vehicle-dashboard">
      {/* Compact Header */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdDashboard className="title-icon" />
            Vehicle Analytics Dashboard
          </h2>
        </div>
        <div className="header-filters-compact">
          <input type="date" className="filter-input-compact" value={dateRange.startDate}
            onChange={e => setDateRange(prev => ({ ...prev, startDate: e.target.value }))} />
          <input type="date" className="filter-input-compact" value={dateRange.endDate}
            onChange={e => setDateRange(prev => ({ ...prev, endDate: e.target.value }))} />
          {locations.length > 0 && (
            <select
              className="filter-select-compact"
              style={{ width: '200px' }}
              value={selectedLocation}
              onChange={e => {
                setSelectedLocation(e.target.value);
                loadDashboardData(e.target.value);
              }}
            >
              <option value="">All Locations</option>
              {locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          )}
          <button className="btn-book-compact" onClick={() => loadDashboardData(selectedLocation)}>
            <MdRefresh />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Scan Analytics Section - First View */}
      {scanData && (
        <>
          <div className="scan-analytics-header">
            <h2>
              <MdQrCodeScanner className="section-icon" />
              Scan Analytics
            </h2>
          </div>

          {/* Scan Summary Cards */}
          <div className="metrics-grid">
            <div className="metric-card scan-metric">
              <h3>Total Scans</h3>
              <p className="metric-value">{scanData.totalScansToday || 0}</p>
              <div className="metric-details">
                <span className="highlight">{scanData.uniqueUsersScannedToday || 0} unique users</span>
              </div>
            </div>

            <div className="metric-card scan-metric">
              <h3>Unique Users</h3>
              <p className="metric-value">{scanData.uniqueUsersScannedToday || 0}</p>
              <div className="metric-details">
                <span>{dateRange.startDate === dateRange.endDate ? dateRange.startDate : `${dateRange.startDate} to ${dateRange.endDate}`}</span>
              </div>
            </div>

            <div className="metric-card scan-metric">
              <h3>Morning Scans</h3>
              <p className="metric-value">{scanData.scansByTiming?.['8:00 AM'] || scanData.scansByTiming?.['Morning'] || scanData.scansByTiming ? Object.entries(scanData.scansByTiming).find(([k]) => k.toLowerCase().includes('morning') || k.includes('8:00'))?.[1] || 0 : 0}</p>
              <div className="metric-details">
                <span>In selected range</span>
              </div>
            </div>

            <div className="metric-card scan-metric">
              <h3>
                <MdTrendingUp style={{ marginRight: '8px' }} />
                Avg Daily
              </h3>
              <p className="metric-value">
                {scanData.dailyTrend && scanData.dailyTrend.length > 0 ? Math.round(scanData.dailyTrend.reduce((sum, d) => sum + d.scanCount, 0) / scanData.dailyTrend.length) : 0}
              </p>
              <div className="metric-details">
                <span>Per day average</span>
              </div>
            </div>
          </div>

          {/* Scan Charts */}
          <div className="charts-grid">
            {dailyTrendChartData && (
              <div className="chart-card wide">
                <h3>Daily Scan Trend</h3>
                <Line
                  data={dailyTrendChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { display: false }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: { stepSize: 1 }
                      }
                    }
                  }}
                />
              </div>
            )}

            {routeScansChartData && scanData.scansByRoute?.length > 0 && (
              <div className="chart-card">
                <h3>Scans by Route</h3>
                <Doughnut
                  data={routeScansChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: 'right',
                        labels: { boxWidth: 12, padding: 10 }
                      }
                    }
                  }}
                />
              </div>
            )}

            {timingChartData && Object.keys(scanData.scansByTiming || {}).length > 0 && (
              <div className="chart-card small">
                <h3>Scans by Timing</h3>
                <Doughnut
                  data={timingChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                  }}
                />
              </div>
            )}
          </div>

          {/* Driver Performance & Vehicle Usage */}
          <div className="scan-details-grid">
            {scanData.scansByDriver && scanData.scansByDriver.length > 0 && (
              <div className="scan-details-card">
                <h3>Driver Performance</h3>
                <div className="scan-list" style={{ maxHeight: '220px', overflowY: 'auto', overflowX: 'hidden' }}>
                  {scanData.scansByDriver.map((driver, idx) => (
                    <div key={idx} className="scan-list-item">
                      <span className="scan-label">{driver.driverName}</span>
                      <span className="scan-count">{driver.scanCount} scans</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {scanData.scansByVehicle && scanData.scansByVehicle.length > 0 && (
              <div className="scan-details-card">
                <h3>Vehicle Usage</h3>
                <div className="scan-list" style={{ maxHeight: '220px', overflowY: 'auto', overflowX: 'hidden' }}>
                  {scanData.scansByVehicle.map((vehicle, idx) => (
                    <div key={idx} className="scan-list-item">
                      <span className="scan-label">{vehicle.vehicleNumber}</span>
                      <span className="scan-count">{vehicle.scanCount} scans ({vehicle.percentage}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Recent Scans Table */}
          {scanData.recentScans && scanData.recentScans.length > 0 && (
            <div className="route-details-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Recent Scans ({dateRange.startDate === dateRange.endDate ? dateRange.startDate : `${dateRange.startDate} to ${dateRange.endDate}`})</h2>
                <button onClick={exportScanData} style={{ padding: '6px 14px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}><MdFileDownload /> Export</button>
              </div>
              <div className="dashboard-table-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', tableLayout: 'fixed' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                    <tr>
                      <th style={{ ...thStyle, width: '9%' }}>Employee ID</th>
                      <th style={{ ...thStyle, width: '22%' }}>Name</th>
                      <th style={{ ...thStyle, width: '22%' }}>Route</th>
                      <th style={{ ...thStyle, width: '12%' }}>Vehicle</th>
                      <th style={{ ...thStyle, width: '8%' }}>Timing</th>
                      <th style={{ ...thStyle, width: '14%' }}>Scan Time</th>
                      <th style={{ ...thStyle, width: '13%' }}>Scanned By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanData.recentScans.map((scan, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={tdStyle}>{scan.empId}</td>
                        <td style={tdStyle} title={scan.employeeName}><strong>{scan.employeeName}</strong></td>
                        <td style={tdStyle} title={scan.route}>{scan.route}</td>
                        <td style={tdStyle}>{scan.vehicle}</td>
                        <td style={tdStyle}>
                          <span className={`timing-badge ${scan.timing?.toLowerCase()}`}>
                            {scan.timing}
                          </span>
                        </td>
                        <td style={tdStyle}>{scan.scanTime}</td>
                        <td style={tdStyle}>{scan.scannedBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {analyticsData && (
        <>
          {/* Route & Capacity Analytics Section */}
          <div className="scan-analytics-header">
            <h2>
              <MdDashboard className="section-icon" />
              Route & Capacity Analytics
            </h2>
          </div>

          {/* Summary Cards */}
          <div className="metrics-grid">
            <div className="metric-card">
              <h3>Total Routes</h3>
              <p className="metric-value">{analyticsData.totalRoutes}</p>
              <div className="metric-details">
                <span>Active routes with assigned vehicles</span>
              </div>
            </div>

            <div className="metric-card">
              <h3>Total Vehicles</h3>
              <p className="metric-value">{analyticsData.totalVehicles}</p>
              <div className="metric-details">
                <span>Capacity: {analyticsData.totalCapacity} seats</span>
              </div>
            </div>

            <div className="metric-card">
              <h3>Assigned Users</h3>
              <p className="metric-value">{analyticsData.totalAssignedUsers}</p>
              <div className="metric-details">
                <span>Users assigned to routes</span>
              </div>
            </div>

            <div className="metric-card">
              <h3>Overall Utilization</h3>
              <p className="metric-value">{analyticsData.overallUtilization?.toFixed(1)}%</p>
              <div className="metric-details">
                <span className={analyticsData.overallUtilization > 100 ? 'over-capacity' : 'normal'}>
                  {analyticsData.overallUtilization > 100 ? 'Over Capacity' :
                   analyticsData.overallUtilization >= 80 ? 'High Utilization' : 'Normal'}
                </span>
              </div>
            </div>
          </div>

          {/* Capacity Overview Cards */}
          <div className="capacity-overview">
            <h2>Capacity Overview</h2>
            <div className="capacity-cards">
              <div className="capacity-card status-over">
                <h4>Over Capacity</h4>
                <p className="count">{analyticsData.capacityBreakdown?.routesOverCapacity || 0}</p>
                <span>routes</span>
              </div>
              <div className="capacity-card status-at">
                <h4>At Capacity</h4>
                <p className="count">{analyticsData.capacityBreakdown?.routesAtCapacity || 0}</p>
                <span>routes</span>
              </div>
              <div className="capacity-card status-optimal">
                <h4>Optimal</h4>
                <p className="count">{analyticsData.capacityBreakdown?.routesOptimal || 0}</p>
                <span>routes</span>
              </div>
              <div className="capacity-card status-under">
                <h4>Under Utilized</h4>
                <p className="count">{analyticsData.capacityBreakdown?.routesUnderUtilized || 0}</p>
                <span>routes</span>
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="charts-grid">
            {capacityBreakdownChartData && (
              <div className="chart-card small">
                <h3>Capacity Distribution</h3>
                <Doughnut
                  data={capacityBreakdownChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                  }}
                />
              </div>
            )}

            {routeCapacityChartData && (
              <div className="chart-card">
                <h3>Route Capacity vs Assigned Users</h3>
                <Bar
                  data={routeCapacityChartData}
                  options={{
                    responsive: true,
                    scales: {
                      x: {
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45
                        }
                      }
                    }
                  }}
                />
              </div>
            )}

            {userDistributionChartData && (
              <div className="chart-card">
                <h3>User Distribution by Route</h3>
                <Bar
                  data={userDistributionChartData}
                  options={{
                    responsive: true,
                    indexAxis: 'y',
                  }}
                />
              </div>
            )}
          </div>

          {/* Actual vs Assigned Vehicle Utilization */}
          {vehicleUtilData && vehicleUtilData.length > 0 && (
            <>
              <div className="scan-analytics-header">
                <h2><MdDashboard className="section-icon" /> Actual Vehicle Utilization (from Scans)</h2>
              </div>
              <div className="metrics-grid">
                <div className="metric-card scan-metric">
                  <h3>Total Vehicles</h3>
                  <p className="metric-value">{vehicleUtilData.length}</p>
                </div>
                <div className="metric-card scan-metric">
                  <h3>Avg Seat Utilization</h3>
                  <p className="metric-value">
                    {vehicleUtilData.length > 0 ? (vehicleUtilData.reduce((s, v) => s + v.seatUtilizationPercent, 0) / vehicleUtilData.length).toFixed(1) : 0}%
                  </p>
                </div>
                <div className="metric-card scan-metric">
                  <h3>Total Unique Employees</h3>
                  <p className="metric-value">
                    {vehicleUtilData.reduce((s, v) => s + (v.uniqueEmployees || 0), 0)}
                  </p>
                </div>
              </div>
              <div className="route-details-section">
                <h2>Vehicle Seat Utilization (Actual Passengers vs Capacity)</h2>
                <div className="dashboard-table-scroll">
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', tableLayout: 'auto' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                      <tr>
                        <th style={thStyle}>Vehicle</th><th style={thStyle}>Type</th><th style={thStyle}>Seats</th><th style={thStyle}>Avg Daily</th><th style={thStyle}>Max Daily</th><th style={thStyle}>Utilization</th><th style={thStyle}>Morning</th><th style={thStyle}>Evening</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehicleUtilData.map((v, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={tdStyle}><strong>{v.registrationNumber}</strong></td>
                          <td style={tdStyle}>{v.vehicleType}</td>
                          <td style={tdStyle}>{v.totalSeats}</td>
                          <td style={tdStyle}>{v.avgDailyPassengers}</td>
                          <td style={tdStyle}>{v.maxDailyPassengers}</td>
                          <td style={tdStyle}>
                            <span className={`status-badge ${v.seatUtilizationPercent > 100 ? 'status-over' : v.seatUtilizationPercent > 60 ? 'status-optimal' : 'status-under'}`}>
                              {v.seatUtilizationPercent}%
                            </span>
                          </td>
                          <td style={tdStyle}><span className="timing-badge morning">{v.morningScans}</span></td>
                          <td style={tdStyle}><span className="timing-badge evening">{v.eveningScans}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Route Details Table */}
          {analyticsData.routeAnalytics && analyticsData.routeAnalytics.length > 0 && (
            <div className="route-details-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Route Details</h2>
                <button onClick={exportRouteDetails} style={{ padding: '6px 14px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}><MdFileDownload /> Export</button>
              </div>
              <div className="dashboard-table-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', tableLayout: 'auto' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                    <tr>
                      <th style={thStyle}>Route</th>
                      <th style={thStyle}>Timing</th>
                      <th style={thStyle}>Vehicles</th>
                      <th style={thStyle}>Capacity</th>
                      <th style={thStyle}>Assigned</th>
                      <th style={thStyle}>Available</th>
                      <th style={thStyle}>Utilization</th>
                      <th style={thStyle}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.routeAnalytics.map((route, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={tdStyle}><strong>{route.routeName}</strong></td>
                        <td style={tdStyle}>{route.timing}</td>
                        <td style={tdStyle}>{route.assignedVehicles?.length || 0}</td>
                        <td style={tdStyle}>{route.totalCapacity}</td>
                        <td style={tdStyle}>{route.assignedUsers}</td>
                        <td style={{ ...tdStyle, color: route.availableSeats < 0 ? '#f44336' : '#4caf50', fontWeight: 600 }}>
                          {route.availableSeats}
                        </td>
                        <td style={tdStyle}>{route.utilizationPercent?.toFixed(1)}%</td>
                        <td style={tdStyle}>
                          <span className={`status-badge ${getCapacityStatusColor(route.capacityStatus)}`}>
                            {getCapacityStatusLabel(route.capacityStatus)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default VehicleDashboard;
